import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Pause, Play, RefreshCw, X } from 'lucide-react';
import { queryClient } from '@/lib/queryClient';
import { createDemoAppointment, createDemoClient, createDemoInvoice, resetDemoState } from './mockData';
import { disableDemoMode, isDemoModeEnabled } from './config';

type DemoMutation = 'none' | 'client' | 'appointment' | 'invoice' | 'recipe' | 'questionnaire' | 'mealPlan';

type DemoInteraction =
  | 'appointment-fill-details'
  | 'appointment-select-client'
  | 'appointment-pick-date'
  | 'appointment-pick-time'
  | 'client-fill-details'
  | 'meal-plan-fill-dialog'
  | 'manual-select-client';

type DemoTargetSpec = {
  selector: string;
  label: string;
  description: string;
  /** Optional scripted interaction performed when this highlight becomes active. */
  interaction?: DemoInteraction;
};

type DemoTourStep = {
  id: string;
  route: string;
  title: string;
  surfaceSelector: string;
  openSelector?: string;
  targetSelectors: DemoTargetSpec[];
  mutation?: DemoMutation;
};

type DemoTarget = DemoTargetSpec & { id: string };

type HighlightBox = {
  top: number;
  left: number;
  width: number;
  height: number;
} | null;

type DemoPhase = 'intro' | 'touring' | 'done';

const steps: DemoTourStep[] = [
  {
    id: 'settings',
    route: '/profile?view=hub&demo=true',
    title: 'Profile Hub',
    surfaceSelector: '[data-demo-id="settings-hub"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="settings-hub"]',
        label: 'Demo Starting Point',
        description:
          'This is the profile and settings hub. From this demo link, the tour starts here first, then moves through the main workspaces one by one.',
      },
      {
        selector: '[data-demo-id="settings-practice-profile"]',
        label: 'Profile & Practice Information',
        description:
          'Your credentials and clinic details — shown on invoices, booking pages, and everything clients see.',
      },
      {
        selector: '[data-demo-id="settings-availability"]',
        label: 'Availability',
        description:
          'Working hours, breaks, and blocked dates. These availability rules power appointment booking.',
      },
      {
        selector: '[data-demo-id="settings-invoice-tax"]',
        label: 'Invoice & Tax Details',
        description:
          'Business details, tax registration, and invoice defaults, so every invoice goes out complete and compliant.',
      },
      {
        selector: '[data-demo-id="settings-packages"]',
        label: 'Packages & Plans',
        description:
          'Define consultation types, multi-session plans, and packages — the building blocks for booking and billing.',
      },
    ],
  },
  {
    id: 'dashboard',
    route: '/dashboard?demo=true',
    title: 'Dashboard',
    surfaceSelector: '[data-demo-id="dashboard-home"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="dashboard-today-tab"]',
        label: 'Today',
        description:
          'Your day at a glance — upcoming appointments, meal plans that need action, and invoices to follow up, all in one operational view.',
      },
      {
        selector: '[data-demo-id="dashboard-practice-insights-tab"]',
        label: 'Practice Insights',
        description:
          'Performance analytics for your practice: revenue trends, client growth, appointment volume, and questionnaire completion rates.',
      },
      {
        selector: '[data-demo-id="dashboard-view-all"]',
        label: 'View All',
        description:
          'Every summary card links to its full workspace, so you can jump straight from an overview into managing the underlying records.',
      },
    ],
  },
  {
    id: 'clients',
    route: '/clients?demo=true',
    title: 'Clients',
    surfaceSelector: '[data-demo-id="clients-page"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="clients-page"]',
        label: 'Clients Main Page',
        description:
          'This is the client workspace. Start here to review client records, filter your list, open profiles, or begin onboarding a new client.',
      },
      {
        selector: '[data-demo-id="clients-add"]',
        label: 'Add Client',
        description:
          'Use Add Client when you are ready to create a new client record. The demo opens this creation flow in the next step.',
      },
      {
        selector: '[data-demo-id="clients-controls"]',
        label: 'Search and Filters',
        description:
          'Use status tabs, search, filters, export, and view controls to narrow the client list before opening a record.',
      },
      {
        selector: '[data-demo-id="clients-list"]',
        label: 'Client List',
        description:
          'Client cards and rows show the people you manage. From here you can open details, schedule care, or continue follow-up work.',
      },
    ],
  },
  {
    id: 'client-creation',
    route: '/clients/add?demo=true',
    title: 'Client Creation',
    surfaceSelector: '[data-demo-id="add-client-page"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="add-client-page"]',
        label: 'Create a New Client',
        description:
          'Start with the essentials: name, contact details, date of birth, and location. Two onboarding paths continue from here.',
        interaction: 'client-fill-details',
      },
      {
        selector: '[data-demo-id="add-client-continue-manual"]',
        label: 'Continue to Add Manually',
        description:
          'Enter body metrics, activity level, and goals yourself — CalorieScience calculates evidence-based nutrition targets instantly.',
      },
      {
        selector: '[data-demo-id="add-client-invite"]',
        label: 'Invite Client to Fill',
        description:
          'Prefer a hands-off intake? Email the client a secure link and let them complete their own details before the first session.',
      },
      {
        selector: '[data-demo-id="add-client-continue-health"]',
        label: 'Continue',
        description:
          'Finish onboarding with optional extras: assign questionnaires, book the first appointment, and set up payment.',
      },
    ],
    mutation: 'client',
  },
  {
    id: 'appointment',
    route: '/appointments?demo=true',
    title: 'Appointments',
    surfaceSelector: '[data-demo-id="appointments-page"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="appointments-page"]',
        label: 'Appointments Main Page',
        description:
          'This main page shows appointment counts, upcoming and past tabs, filters, and the appointment list before you open any scheduling form.',
      },
      {
        selector: '[data-demo-id="appointments-create"]',
        label: 'New Appointment',
        description:
          'This button opens the scheduler. The tour explains the main page first, then opens this scheduler in the next step.',
      },
      {
        selector: '[data-action-tour-id="appointments-calendar"]',
        label: 'Calendar View',
        description:
          'Use this to switch from the list into the calendar page when you want a day, week, or month view.',
      },
      {
        selector: '[data-action-tour-id="appointments-tabs"]',
        label: 'Appointment Tabs',
        description:
          'Switch between all appointments, upcoming sessions, and past sessions. Counts update beside each tab.',
      },
      {
        selector: '[data-action-tour-id="appointments-filters"]',
        label: 'Mode and Date Filters',
        description:
          'Filter by online or in-person sessions, or narrow the appointment list to a date range.',
      },
    ],
  },
  {
    id: 'appointment-scheduler',
    route: '/appointments?demo=true',
    title: 'Appointment Scheduler',
    surfaceSelector: '[data-demo-id="appointments-page"]',
    openSelector: '[data-demo-id="appointments-create"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="appointment-scheduler-dialog"]',
        label: 'Schedule a Session',
        description:
          'Book a client session in seconds: name it, choose who it is for, then pick from your genuinely available time slots.',
      },
      {
        selector: '[data-demo-id="appointment-scheduler-session"]',
        label: 'Session Details',
        description:
          'Set the appointment name, agenda notes, duration, and whether the session runs online or in person at your clinic.',
        interaction: 'appointment-fill-details',
      },
      {
        selector: '[data-demo-id="appointment-scheduler-client"]',
        label: 'Select a Client',
        description:
          'Search your client list and link the session to the right record — their history, plans, and billing stay connected.',
        interaction: 'appointment-select-client',
      },
      {
        selector: '[data-demo-id="appointment-scheduler-calendar"]',
        label: 'Booking Calendar',
        description:
          'Only days you actually work are selectable — the calendar respects your working hours, breaks, and blocked dates.',
        interaction: 'appointment-pick-date',
      },
      {
        selector: '[data-demo-id="appointment-scheduler-times"]',
        label: 'Available Times',
        description:
          'Open slots for the selected date, sized to the session duration and automatically cleared of conflicts with existing bookings.',
        interaction: 'appointment-pick-time',
      },
      {
        selector: '[data-demo-id="appointment-scheduler-questionnaires"]',
        label: 'Attach Questionnaires',
        description:
          'Send intake or check-in forms together with the booking, so clients arrive prepared and you arrive informed.',
      },
      {
        selector: '[data-demo-id="appointment-scheduler-submit"]',
        label: 'Create Appointment',
        description:
          'Confirms the session, notifies the client, and places it on your calendar — reminders and meeting links included.',
      },
    ],
    mutation: 'appointment',
  },
  {
    id: 'meal-planning',
    route: '/meal-planning?demo=true',
    title: 'Meal Planning',
    surfaceSelector: '[data-demo-id="meal-planning-page"]',
    openSelector: '[data-demo-id="meal-plan-add"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="meal-planning-page"]',
        label: 'Meal Plan Library',
        description:
          'All of your plans in one place — manual, AI-assisted, and reusable generic templates — ready to review, assign, export, or archive.',
      },
      {
        selector: '[data-demo-id="meal-plan-dialog"]',
        label: 'Add Meal Plan',
        description:
          'Name the plan, set its length, and choose how to build it — every new plan starts from this dialog.',
        interaction: 'meal-plan-fill-dialog',
      },
      {
        selector: '[data-demo-id="meal-plan-option-manual"]',
        label: 'Manual Meal Planning',
        description:
          'Build the plan by hand: pick recipes and ingredients meal by meal, with live nutrition totals against the client’s targets.',
      },
      {
        selector: '[data-demo-id="meal-plan-option-ai"]',
        label: 'AI Generated Meal Plan',
        description:
          'Let AI draft the full plan from the client’s calculated calorie and macro targets — then review and adjust any meal before it goes out.',
      },
    ],
    mutation: 'mealPlan',
  },
  {
    id: 'manual-plan',
    route: '/manual-meal-plan?clientId=demo-client-1&name=Balanced-Week&days=7&demo=true',
    title: 'Manual Plan Setup',
    surfaceSelector: '[data-demo-id="manual-plan-page"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="manual-plan-page"]',
        label: 'Manual Meal Plan',
        description:
          'Choosing Manual brings you here: set the plan basics — who it is for, how long it runs, and what to call it.',
      },
      {
        selector: '[data-demo-id="manual-plan-client"]',
        label: 'Select Client',
        description:
          'Choose who the plan is for — their calorie targets, allergies, intolerances, and conditions load automatically.',
        interaction: 'manual-select-client',
      },
      {
        selector: '[data-demo-id="manual-plan-client-summary"]',
        label: 'Client Context',
        description:
          'Manual planning also starts with the selected client context: BMR, EER, allergies, dietary preferences, medical conditions, and target calories.',
      },
      {
        selector: '[data-demo-id="manual-plan-dates"]',
        label: 'Plan Date Range',
        description:
          'Pick the start and end dates the plan covers — the number of plan days updates to match.',
      },
      {
        selector: '[data-demo-id="manual-plan-goals"]',
        label: 'Calories and Preferences',
        description:
          'This panel is where you review the client targets before manual planning. Use the edit controls here to change calories, macros, and dietary preferences for this meal plan.',
      },
      {
        selector: '[data-demo-id="ai-plan-edit-calories"]',
        label: 'Edit Calories and Macros',
        description:
          'Open this to change the daily calorie target and macro goals before creating the manual meal plan.',
      },
      {
        selector: '[data-demo-id="ai-plan-edit-preferences"]',
        label: 'Edit Dietary Preferences',
        description:
          'Open this to adjust cuisine preferences, dietary preferences, allergies, and intolerances before creating the plan.',
      },
      {
        selector: '[data-demo-id="manual-plan-meal-slots"]',
        label: 'Meal Slots',
        description:
          'Set the meal names, times, meal types, and optional target calories that the manual plan should follow.',
      },
      {
        selector: '[data-demo-id="manual-plan-create"]',
        label: 'Create Meal Plan',
        description:
          'Creates the plan and opens the nutritional analysis editor — exactly where the tour goes next.',
      },
    ],
  },
  {
    id: 'nutrition-editor',
    route: '/meal-plans/demo-meal-plan-3/clients/demo-client-4/nutrition/1?demo=true',
    title: 'Nutritional Analysis',
    surfaceSelector: '[data-demo-id="nutrition-client-banner"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="nutrition-client-banner"]',
        label: 'Client Nutrition Summary',
        description:
          'BMR, EER, dietary preferences, medical conditions, and the daily calorie target — everything guiding this plan sits at the top.',
      },
      {
        selector: '[data-demo-id="meal-plan-dietary-preferences"]',
        label: 'Edit Dietary Preferences',
        description:
          'Open Dietary Preferences to adjust cuisine preferences and diet choices before building or reviewing the meal plan.',
      },
      {
        selector: '[data-demo-id="nutrition-add-meal"]',
        label: 'Add Meal',
        description:
          'Add meal slots to the day, then fill them with recipes or individual foods — macros total live against the client’s target.',
      },
      {
        selector: '[data-demo-id="nutrition-finalise"]',
        label: 'Finalise Meal Plan',
        description:
          'Once every day is filled, finalising locks the plan so it can be exported, shared, or assigned to the client.',
      },
    ],
  },
  {
    id: 'plan-board',
    route: '/meal-planning/client/demo-client-4/meal-plan?category=manual&mealPlanId=demo-meal-plan-3&demo=true',
    title: 'Plan Days',
    surfaceSelector: '[data-demo-id="meal-plan-board"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="meal-plan-board"]',
        label: 'Day-by-Day Overview',
        description:
          'Every day of the plan side by side, with calories and macros scored against the client’s targets and the full meal schedule below.',
      },
      {
        selector: '[data-demo-id="meal-plan-dietary-preferences"]',
        label: 'Dietary Preferences',
        description:
          'This summary can be opened to edit dietary preferences while reviewing the meal plan, so the plan stays aligned with the client.',
      },
      {
        selector: '[data-demo-id="meal-plan-calorie-intake-card"]',
        label: 'Calories Intake',
        description:
          'Each day shows actual calorie intake against the client’s target, so you can quickly see whether the plan is under, over, or on target.',
      },
      {
        selector: '[aria-label="Copy meals from another day"]',
        label: 'Copy Day',
        description:
          'Duplicate a finished day’s meals onto another day in one click — perfect for repeating a well-balanced day.',
      },
      {
        selector: '[data-demo-id="meal-plan-add-day"]',
        label: 'Add Day',
        description:
          'Extend the plan up to 7 days: start a blank day, import a template, or copy an existing day.',
      },
      {
        selector: '[data-demo-id="meal-plan-finalize"]',
        label: 'Finalize Plan',
        description:
          'Marks the draft complete — ready to export as a PDF, share with the client, or track against their progress.',
      },
    ],
  },
  {
    id: 'ai-plan-setup',
    route: '/ai-meal-planning?clientId=demo-client-1&name=AI-Support-Week&days=7&demo=true',
    title: 'AI Meal Plan Setup',
    surfaceSelector: '[data-demo-id="ai-plan-page"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="ai-plan-page"]',
        label: 'AI Meal Plan',
        description:
          'After the manual workflow, AI Meal Plan starts here. The setup page uses the client profile, dates, goals, preferences, and meal slots.',
      },
      {
        selector: '[data-demo-id="ai-plan-client-summary"]',
        label: 'Client Context',
        description:
          'The AI uses the selected client’s profile, dietary preferences, medical conditions, and target calories as the starting context.',
      },
      {
        selector: '[data-demo-id="ai-plan-dates"]',
        label: 'Plan Date Range',
        description:
          'Choose the plan name and date range. AI meal plans stay inside the allowed day limit for the generator.',
      },
      {
        selector: '[data-demo-id="ai-plan-goals"]',
        label: 'Calories and Preferences',
        description:
          'This panel is where you review the client targets before generation. Use the edit controls here to change calories, macros, and dietary preferences for the AI meal plan.',
      },
      {
        selector: '[data-demo-id="ai-plan-edit-calories"]',
        label: 'Edit Calories and Macros',
        description:
          'Open this to change the daily calorie target and macro goals before generating the AI meal plan.',
      },
      {
        selector: '[data-demo-id="ai-plan-edit-preferences"]',
        label: 'Edit Dietary Preferences',
        description:
          'Open this to adjust cuisine preferences, dietary preferences, allergies, and intolerances before generation.',
      },
      {
        selector: '[data-demo-id="ai-plan-meal-slots"]',
        label: 'Meal Slots',
        description:
          'Use client meal slots when you want AI to follow a specific breakfast, lunch, dinner, or snack structure.',
      },
      {
        selector: '[data-demo-id="ai-plan-generate"]',
        label: 'Generate AI Meal Plan',
        description:
          'Generate creates the draft plan. The next demo step opens a ready draft so the review actions can be explained clearly.',
      },
    ],
  },
  {
    id: 'ai-plan-draft',
    route: '/ai-meal-plan/draft/demo-meal-plan-1/client/demo-client-1?demo=true',
    title: 'AI Meal Plan Review',
    surfaceSelector: '[data-demo-id="ai-plan-draft-page"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="ai-plan-draft-page"]',
        label: 'AI Draft Review',
        description:
          'This is the generated AI meal plan draft. Review the meals, compare options, make edits, and only finalise when it is ready.',
      },
      {
        selector: '[data-demo-id="ai-plan-draft-client-summary"]',
        label: 'Generated For This Client',
        description:
          'The summary confirms the client context and how many generated meals are selected in the draft.',
      },
      {
        selector: '[data-demo-id="ai-plan-draft-days"]',
        label: 'Select and Review',
        description:
          'Move through each day, inspect meal options, view recipes, and choose the best meals before approval.',
      },
      {
        selector: '[data-demo-id="ai-plan-draft-regenerate"]',
        label: 'Regenerate',
        description:
          'Regenerate asks AI for a new option for a meal when the current suggestion is not the right fit.',
      },
      {
        selector: '[data-demo-id="ai-plan-draft-smart-edit"]',
        label: 'Smart Edit',
        description:
          'Smart Edit lets you give a targeted prompt, like making a meal higher protein, lower carb, or better aligned to preferences.',
      },
      {
        selector: '[data-demo-id="ai-plan-draft-finalise"]',
        label: 'Finalise AI Meal Plan',
        description:
          'Finalise saves the reviewed AI plan so it can be shared, exported, or used with the client.',
      },
    ],
  },
  {
    id: 'recipes',
    route: '/recipes?demo=true',
    title: 'Recipes',
    surfaceSelector: '[data-demo-id="recipes-page"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="recipes-page"]',
        label: 'Recipes Main Page',
        description:
          'Start from the recipe library first. This page lets you search, filter, view, edit, copy, or delete recipes.',
      },
      {
        selector: '[data-demo-id="recipes-create"]',
        label: 'Create Recipe',
        description:
          'Use this button to open the create-recipe form. The tour moves there next and explains each section.',
      },
      {
        selector: '[data-action-tour-id="recipes-tabs"]',
        label: 'Recipe Tabs',
        description:
          'All shows the full library. My Recipes narrows the list to recipes created by your account.',
      },
      {
        selector: '[data-action-tour-id="recipes-filters"]',
        label: 'Search and Filters',
        description:
          'Find recipes by name or filter by health labels, diet labels, cuisine, and meal type.',
      },
    ],
  },
  {
    id: 'create-recipe',
    route: '/recipes/create?demo=true',
    title: 'Create Recipe',
    surfaceSelector: '[data-demo-id="create-recipe-page"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="recipe-info-section"]',
        label: 'Recipe Details',
        description:
          'Name the recipe and set servings, meal type, cooking method, timing, and an optional photo — everything a plan needs to present it well.',
      },
      {
        selector: '[data-demo-id="recipe-ingredients-section"]',
        label: 'Ingredients',
        description:
          'Search the food database and set portions — nutrition totals, allergens, and intolerances are derived automatically as you build.',
      },
      {
        selector: '[data-demo-id="recipe-instructions-section"]',
        label: 'Instructions',
        description:
          'Add preparation steps so the finished recipe is ready to drop into any meal plan or share with a client.',
      },
      {
        selector: '[data-demo-id="recipe-create"]',
        label: 'Create Recipe',
        description:
          'Saves the recipe to your library with its full nutrition profile, ready for meal planning and client sharing.',
      },
    ],
    mutation: 'recipe',
  },
  {
    id: 'questionnaires',
    route: '/questionnaires?tab=core&coreFilter=all&view=grid&demo=true',
    title: 'Questionnaires',
    surfaceSelector: '[data-demo-id="questionnaires-page"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="questionnaires-page"]',
        label: 'Questionnaires Main Page',
        description:
          'Start here first. This page shows questionnaire totals, core and custom tabs, and the main actions for previewing, assigning, and reviewing responses.',
      },
      {
        selector: '[data-demo-id="questionnaires-core-tab"]',
        label: 'Core Questionnaires',
        description:
          'Ready-made intake, monitoring, and condition-specific forms — preview, assign, and track them without building anything.',
      },
      {
        selector: '[data-demo-id="questionnaires-custom-tab"]',
        label: 'Custom Questionnaires',
        description:
          'Build your own forms for your specialty: draft, publish, assign to clients, and review responses as they come in.',
      },
      {
        selector: '[data-action-tour-id="questionnaires-preview"]',
        label: 'Preview Button',
        description:
          'Preview opens the questionnaire detail page so you can view what the client will answer before sharing it.',
      },
      {
        selector: '[data-action-tour-id="questionnaires-responses"]',
        label: 'Responses Button',
        description:
          'Responses opens the tracking page where you copy share links, fill manually, send reminders, and view submitted answers.',
      },
      {
        selector: '[data-action-tour-id="questionnaires-assign"]',
        label: 'Assign Button',
        description:
          'Assign opens the client-selection page where you choose who receives the questionnaire.',
      },
    ],
    mutation: 'questionnaire',
  },
  {
    id: 'questionnaire-preview',
    route: '/questionnaires/demo-core-intake?demo=true',
    title: 'Questionnaire Preview',
    surfaceSelector: '[data-demo-id="questionnaire-detail-page"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="questionnaire-detail-page"]',
        label: 'Preview Page',
        description:
          'This page shows the questionnaire content before you send it. Use it to review the structure and confirm it is the right form.',
      },
    ],
  },
  {
    id: 'questionnaire-assignment',
    route: '/questionnaires/demo-core-intake/assign?demo=true',
    title: 'Assign Questionnaire',
    surfaceSelector: '[data-demo-id="questionnaire-assignment-page"]',
    targetSelectors: [
      {
        selector: '[data-action-tour-id="questionnaire-assignment-search"]',
        label: 'Search Clients',
        description:
          'Find the client who should receive this questionnaire.',
      },
      {
        selector: '[data-action-tour-id="questionnaire-assignment-select"]',
        label: 'Select Clients',
        description:
          'Select one or more clients. The final Assign button stays disabled until a client is selected.',
      },
      {
        selector: '[data-action-tour-id="questionnaire-assignment-deadline"]',
        label: 'Deadline',
        description:
          'Add an optional due date per selected client.',
      },
      {
        selector: '[data-action-tour-id="questionnaire-assignment-message"]',
        label: 'Personal Message',
        description:
          'Add an optional client-specific message before sending.',
      },
      {
        selector: '[data-action-tour-id="questionnaire-assignment-delivery"]',
        label: 'Delivery Method',
        description:
          'Choose Email when the system should send the questionnaire link to the selected client.',
      },
      {
        selector: '[data-action-tour-id="questionnaire-assignment-submit"]',
        label: 'Assign Questionnaire',
        description:
          'This sends the questionnaire and makes it available on the responses page.',
      },
    ],
  },
  {
    id: 'questionnaire-responses',
    route: '/questionnaires/demo-core-intake/responses?demo=true',
    title: 'Questionnaire Responses',
    surfaceSelector: '[data-demo-id="questionnaire-responses-page"]',
    targetSelectors: [
      {
        selector: '[data-action-tour-id="questionnaire-responses-search"]',
        label: 'Find a Client Response',
        description:
          'Search clients on this questionnaire when you need one client status or submission quickly.',
      },
      {
        selector: '[data-action-tour-id="questionnaire-responses-copy-link"]',
        label: 'Copy Share Link',
        description:
          'Copy Link gives you the client questionnaire URL to share when the client should complete it themselves.',
      },
      {
        selector: '[data-action-tour-id="questionnaire-responses-fill-manually"]',
        label: 'Fill Manually',
        description:
          'Fill Manually opens practitioner-entry mode so answers can be entered during a call or appointment.',
      },
      {
        selector: '[data-action-tour-id="questionnaire-responses-row-action"]',
        label: 'View or Send Reminder',
        description:
          'Completed rows show View for submitted answers. Pending rows show Send Reminder to email the client again.',
      },
    ],
  },
  {
    id: 'client-details',
    route: '/clients/demo-client-1/details-v2/overview?demo=true',
    title: 'Client Profile',
    surfaceSelector: '[data-demo-id="client-details-shell"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="client-details-shell"]',
        label: 'The Complete Client Record',
        description:
          'Everything about one client in a single workspace: demographics, anthropometrics, nutrition targets, appointments, questionnaires, meal plans, notes, and billing history.',
      },
    ],
  },
  {
    id: 'invoicing',
    route: '/invoicing?demo=true',
    title: 'Invoicing',
    surfaceSelector: '[data-demo-id="invoicing-page"]',
    openSelector: '[data-demo-id="invoicing-create"]',
    targetSelectors: [
      {
        selector: '[data-demo-id="invoice-flow-dialog"]',
        label: 'Generate an Invoice',
        description:
          'Create a polished invoice in one flow: pick the client, add services or packages, and taxes, discounts, and totals compute automatically.',
      },
      {
        selector: '[data-demo-id="invoice-flow-steps"]',
        label: 'Guided Steps',
        description:
          'A clear path from invoice details to a live preview, so nothing goes out until you have seen exactly what the client will receive.',
      },
      {
        selector: '[data-demo-id="invoice-flow-preview"]',
        label: 'Preview Invoice',
        description:
          'Validates the client, dates, line items, and totals, then renders the final document exactly as it will be delivered.',
      },
      {
        selector: '[data-demo-id="invoice-flow-confirm"]',
        label: 'Confirm & Create',
        description:
          'Issues the invoice and unlocks one-click actions: download the PDF, print it, email it, or track its payment status.',
      },
    ],
    mutation: 'invoice',
  },
];

const TARGET_ADVANCE_MS = 9000;
const INTRO_SEEN_STORAGE_KEY = 'caloriescience_demo_tour_intro_seen';

const hasSeenIntro = () => {
  try {
    return window.sessionStorage.getItem(INTRO_SEEN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const markIntroSeen = () => {
  try {
    window.sessionStorage.setItem(INTRO_SEEN_STORAGE_KEY, 'true');
  } catch {
    // Session storage unavailable; the intro will simply show again.
  }
};
const EMPTY_POLL_MS = 600;
const EMPTY_POLL_MAX_ATTEMPTS = 25;
const INTERACTION_DELAY_MS = 900;
// Next-clicks landing while a freshly entered step is still rendering must not
// skip the step; only treat them as an explicit skip once the step had time to load.
const EMPTY_STEP_MANUAL_SKIP_MS = 4000;

const findTarget = (selector: string): HTMLElement | null => document.querySelector(selector);

const isRenderedElement = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  const style = window.getComputedStyle(element);
  return rect.width >= 8 && rect.height >= 8 && style.visibility !== 'hidden' && style.display !== 'none';
};

const collectTargets = (step: DemoTourStep): DemoTarget[] =>
  step.targetSelectors
    .map((target, index) => {
      const element = findTarget(target.selector);
      if (!element || !isRenderedElement(element)) return null;
      return { ...target, id: `${step.id}-${index}` };
    })
    .filter((target): target is DemoTarget => Boolean(target));

const setNativeInputValue = (input: HTMLInputElement | HTMLTextAreaElement, value: string) => {
  const prototype =
    input instanceof HTMLTextAreaElement ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
  setter?.call(input, value);
  input.dispatchEvent(new Event('input', { bubbles: true }));
};

const clickElement = (element: HTMLElement | null | undefined) => {
  if (!element) return;
  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  element.click();
};

const runDemoInteraction = (interaction: DemoInteraction) => {
  try {
    if (interaction === 'appointment-fill-details') {
      const title = document.querySelector<HTMLInputElement>('[data-demo-id="appointment-scheduler-title"]');
      if (title && !title.value) setNativeInputValue(title, 'Initial Consultation');
      const description = document.querySelector<HTMLTextAreaElement | HTMLInputElement>(
        '[data-demo-id="appointment-scheduler-description"]',
      );
      if (description && !description.value) {
        setNativeInputValue(description, 'Review goals, current diet, and agree the first meal plan.');
      }
      return;
    }

    if (interaction === 'appointment-select-client') {
      const search = document.querySelector<HTMLInputElement>('[data-demo-id="appointment-scheduler-client-search"]');
      if (!search || search.value) return;
      search.focus();
      setNativeInputValue(search, 'Amelia');
      let attempts = 0;
      const interval = window.setInterval(() => {
        attempts += 1;
        const option = Array.from(
          document.querySelectorAll<HTMLElement>('[data-demo-id="appointment-scheduler-client-option"]'),
        ).find((element) => (element.textContent || '').includes('Amelia'));
        if (option) {
          window.clearInterval(interval);
          clickElement(option);
        } else if (attempts >= 8) {
          window.clearInterval(interval);
        }
      }, 500);
      return;
    }

    if (interaction === 'appointment-pick-date') {
      const dialog = document.querySelector<HTMLElement>('[data-demo-id="appointment-scheduler-dialog"]');
      if (!dialog) return;
      const alreadySelectedTime = dialog.querySelector('[data-demo-id="appointment-scheduler-times"] [aria-pressed="true"]');
      if (alreadySelectedTime) return;
      const day = Array.from(dialog.querySelectorAll<HTMLButtonElement>('button:not([disabled])')).find((button) =>
        /^\d{1,2}$/.test((button.textContent || '').trim()),
      );
      clickElement(day);
      return;
    }

    if (interaction === 'client-fill-details') {
      const page = document.querySelector<HTMLElement>('[data-demo-id="add-client-page"]');
      if (!page) return;
      const fill = (selector: string, value: string) => {
        const input = page.querySelector<HTMLInputElement>(selector);
        if (input && !input.value) setNativeInputValue(input, value);
      };
      fill('input[placeholder="First Name"]', 'Olivia');
      fill('input[placeholder="Last Name"]', 'Bennett');
      fill('input[placeholder="Enter your client\'s email"]', 'olivia.bennett@example.com');
      fill('input[placeholder="e.g. Hyderabad"]', 'London');
      fill('input[placeholder="Zip code"]', 'SW1A 1AA');
      fill('input[type="date"]', '1992-04-18');
      return;
    }

    if (interaction === 'meal-plan-fill-dialog') {
      const dialog = document.querySelector<HTMLElement>('[data-demo-id="meal-plan-dialog"]');
      if (!dialog) return;
      const nameInput = dialog.querySelector<HTMLInputElement>('input[placeholder="Enter meal name"]');
      if (nameInput && !nameInput.value) setNativeInputValue(nameInput, 'Balanced Week');
      const search = dialog.querySelector<HTMLInputElement>('input[placeholder="Search Clients..."]');
      if (!search || search.value) return;
      search.focus();
      setNativeInputValue(search, 'Amelia');
      let attempts = 0;
      const interval = window.setInterval(() => {
        attempts += 1;
        // Client options render inside a MUI Popover portal, outside the dialog node.
        const option = Array.from(document.querySelectorAll<HTMLElement>('.MuiPopover-paper p')).find((element) =>
          (element.textContent || '').includes('Amelia'),
        );
        if (option) {
          window.clearInterval(interval);
          clickElement(option);
        } else if (attempts >= 8) {
          window.clearInterval(interval);
        }
      }, 500);
      return;
    }

    if (interaction === 'manual-select-client') {
      const container = document.querySelector<HTMLElement>('[data-demo-id="manual-plan-client"]');
      const input = container?.querySelector<HTMLInputElement>('input');
      if (!input || input.value) return;
      input.focus();
      setNativeInputValue(input, 'Priya');
      let attempts = 0;
      const interval = window.setInterval(() => {
        attempts += 1;
        const option = Array.from(document.querySelectorAll<HTMLElement>('li, [role="option"]')).find((element) =>
          (element.textContent || '').includes('Priya'),
        );
        if (option) {
          window.clearInterval(interval);
          clickElement(option);
        } else if (attempts >= 8) {
          window.clearInterval(interval);
        }
      }, 500);
      return;
    }

    if (interaction === 'appointment-pick-time') {
      const panel = document.querySelector<HTMLElement>('[data-demo-id="appointment-scheduler-times"]');
      if (!panel) return;
      const slot = Array.from(panel.querySelectorAll<HTMLButtonElement>('button:not([disabled])')).find((button) =>
        /\d{1,2}:\d{2}/.test(button.textContent || ''),
      );
      clickElement(slot);
    }
  } catch {
    // Scripted interactions are best-effort; the tour continues either way.
  }
};

const DemoSpotlight = ({ box }: { box: HighlightBox }) => {
  if (!box) return null;
  return (
    <div
      className="pointer-events-none fixed z-[9997] rounded-lg border-2 border-[#20AEDC] bg-[#20AEDC]/5 shadow-[0_0_0_9999px_rgba(15,23,42,0.28),0_0_26px_rgba(32,174,220,0.42)] transition-all duration-700 ease-out"
      style={{
        top: Math.max(8, box.top - 6),
        left: Math.max(8, box.left - 6),
        width: box.width + 12,
        height: box.height + 12,
      }}
    />
  );
};

const DemoTooltip = ({
  box,
  target,
  stepTitle,
  targetIndex,
  targetCount,
  isLastStep,
  onBack,
  onNext,
}: {
  box: HighlightBox;
  target?: DemoTarget;
  stepTitle: string;
  targetIndex: number;
  targetCount: number;
  isLastStep: boolean;
  onBack: () => void;
  onNext: () => void;
}) => {
  if (!box || !target) return null;

  const width = 340;
  const estimatedHeight = 200;
  const coversMostOfViewport = box.width > window.innerWidth * 0.7 && box.height > window.innerHeight * 0.55;

  let top: number;
  let left: number;
  if (coversMostOfViewport) {
    top = window.innerHeight - estimatedHeight - 28;
    left = (window.innerWidth - width) / 2;
  } else {
    const preferLeft = box.left > window.innerWidth * 0.58;
    top = Math.min(Math.max(16, box.top), window.innerHeight - estimatedHeight - 20);
    left = preferLeft
      ? Math.max(16, box.left - width - 18)
      : Math.min(window.innerWidth - width - 16, box.left + box.width + 18);
  }

  const onLastHighlight = targetIndex >= targetCount - 1;
  const nextLabel = onLastHighlight ? (isLastStep ? 'Finish' : 'Next feature') : 'Next';

  return (
    <div
      className="demo-mode-ui pointer-events-auto fixed z-[9998] rounded-xl border border-slate-200 bg-white px-4 py-3.5 shadow-2xl transition-all duration-500 ease-out"
      style={{ top, left, width }}
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#20AEDC]">
        {stepTitle} · {Math.min(targetIndex + 1, Math.max(1, targetCount))} of {Math.max(1, targetCount)}
      </p>
      <h3 className="mt-1.5 text-sm font-semibold leading-5 text-slate-950">{target.label}</h3>
      <p className="mt-1 text-[13px] leading-[1.45] text-slate-600">{target.description}</p>
      <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2.5">
        <button
          type="button"
          className="inline-flex h-8 items-center gap-1 rounded-md px-2.5 text-[13px] font-semibold text-slate-500 transition-colors hover:bg-slate-50"
          onClick={onBack}
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </button>
        <button
          type="button"
          className="inline-flex h-8 items-center gap-1 rounded-md bg-[#20AEDC] px-3.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#168bb0]"
          onClick={onNext}
        >
          {nextLabel}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

const DemoLoadingHint = ({ stepTitle }: { stepTitle: string }) => (
  <div className="demo-mode-ui pointer-events-none fixed bottom-8 left-1/2 z-[9998] -translate-x-1/2 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-2xl">
    <p className="flex items-center gap-2 text-[13px] font-semibold text-slate-700">
      <RefreshCw className="h-3.5 w-3.5 animate-spin text-[#20AEDC]" />
      Loading {stepTitle}…
    </p>
  </div>
);

const DemoOverlayCard = ({
  eyebrow,
  title,
  body,
  children,
}: {
  eyebrow: string;
  title: string;
  body: string;
  children: ReactNode;
}) => (
  <div className="demo-mode-ui fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm">
    <div className="w-[min(540px,100%)] rounded-2xl border border-slate-200 bg-white p-7 text-center shadow-2xl">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#20AEDC]">{eyebrow}</p>
      <h2 className="mt-2.5 text-xl font-semibold leading-7 text-slate-950">{title}</h2>
      <p className="mt-2.5 text-sm leading-[1.55] text-slate-600">{body}</p>
      <div className="mt-6 flex flex-col items-stretch justify-center gap-2 sm:flex-row">{children}</div>
    </div>
  </div>
);

const overlayPrimaryButton =
  'inline-flex h-10 min-w-[132px] shrink-0 items-center justify-center whitespace-nowrap rounded-lg bg-[#20AEDC] px-5 text-sm font-semibold leading-none text-white transition-colors hover:bg-[#168bb0]';
const overlaySecondaryButton =
  'inline-flex h-10 min-w-[132px] shrink-0 items-center justify-center whitespace-nowrap rounded-lg border border-slate-200 px-5 text-sm font-semibold leading-none text-slate-600 transition-colors hover:bg-slate-50';

const DemoTopBar = ({
  activeIndex,
  targetIndex,
  targetCount,
  paused,
  progress,
  onBack,
  onNext,
  onTogglePause,
  onRestart,
  onExit,
}: {
  activeIndex: number;
  targetIndex: number;
  targetCount: number;
  paused: boolean;
  progress: number;
  onBack: () => void;
  onNext: () => void;
  onTogglePause: () => void;
  onRestart: () => void;
  onExit: () => void;
}) => {
  const step = steps[activeIndex];
  const iconButton =
    'inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className="demo-mode-ui fixed left-1/2 top-4 z-[9999] w-[min(600px,calc(100vw-2rem))] -translate-x-1/2 rounded-xl border border-slate-200 bg-white/95 px-3.5 py-2.5 shadow-xl backdrop-blur">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold uppercase tracking-[0.08em] text-[#20AEDC]">
            CalorieScience Tour · {step.title}
          </p>
          <p className="truncate text-xs text-slate-500">
            {paused
              ? 'Paused — explore freely, then press play to resume the tour.'
              : targetCount === 0
                ? `Feature ${activeIndex + 1} of ${steps.length} · Loading…`
                : `Feature ${activeIndex + 1} of ${steps.length} · Highlight ${Math.min(targetIndex + 1, targetCount)} of ${targetCount}`}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            className={iconButton}
            onClick={onBack}
            disabled={activeIndex === 0 && targetIndex === 0}
            aria-label="Previous highlight"
            title="Previous highlight"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={iconButton}
            onClick={onTogglePause}
            aria-label={paused ? 'Resume tour' : 'Pause tour'}
            title={paused ? 'Resume tour' : 'Pause and explore'}
          >
            {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </button>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#20AEDC] text-white transition-colors hover:bg-[#168bb0]"
            onClick={onNext}
            aria-label="Next highlight"
            title="Next highlight"
          >
            <ArrowRight className="h-4 w-4" />
          </button>
          <button type="button" className={iconButton} onClick={onRestart} aria-label="Restart tour" title="Restart tour">
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100"
            onClick={onExit}
            aria-label="Exit demo mode"
            title="Exit demo mode"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="mt-2 h-1 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-[#20AEDC] transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

export const DemoModeProvider = () => {
  const enabled = isDemoModeEnabled();
  const navigate = useNavigate();
  const location = useLocation();
  // A reload mid-demo skips the welcome card and resumes in paused explore mode.
  const [phase, setPhase] = useState<DemoPhase>(() => (hasSeenIntro() ? 'touring' : 'intro'));
  const [activeIndex, setActiveIndex] = useState(0);
  const [targetIndex, setTargetIndex] = useState(0);
  const [targets, setTargets] = useState<DemoTarget[]>([]);
  const [paused, setPaused] = useState(() => hasSeenIntro());
  const [box, setBox] = useState<HighlightBox>(null);
  const mutationRunRef = useRef<Set<string>>(new Set());
  const lastAdvancedIndexRef = useRef(-1);
  const openRunRef = useRef<Set<string>>(new Set());
  const interactionRunRef = useRef<Set<string>>(new Set());
  const stepEnteredAtRef = useRef<number>(Date.now());
  const step = steps[activeIndex];
  const activeTarget = targets[targetIndex];
  const touring = enabled && phase === 'touring';

  const totalHighlights = steps.reduce((sum, entry) => sum + entry.targetSelectors.length, 0);
  const highlightsBefore = steps.slice(0, activeIndex).reduce((sum, entry) => sum + entry.targetSelectors.length, 0);
  const progress = Math.min(100, Math.round(((highlightsBefore + targetIndex + 1) / totalHighlights) * 100));

  const goToStep = useCallback(
    (nextIndex: number) => {
      const clamped = Math.min(Math.max(nextIndex, 0), steps.length - 1);
      const nextStep = steps[clamped];
      // Entering a step is always a fresh visit: its dialog must open again and
      // its scripted interactions must replay (dialogs unmount on route change).
      if (nextStep) {
        openRunRef.current.delete(nextStep.id);
        for (const key of Array.from(interactionRunRef.current)) {
          if (key.startsWith(`${nextStep.id}:`)) interactionRunRef.current.delete(key);
        }
      }
      stepEnteredAtRef.current = Date.now();
      setActiveIndex(clamped);
      setTargetIndex(0);
      setTargets([]);
      setBox(null);
      if (nextStep && `${location.pathname}${location.search}` !== nextStep.route) {
        navigate(nextStep.route);
      }
    },
    [location.pathname, location.search, navigate],
  );

  const runMutation = useCallback((mutation: DemoMutation | undefined, stepId: string) => {
    if (!mutation || mutation === 'none' || mutationRunRef.current.has(stepId)) return;
    mutationRunRef.current.add(stepId);
    if (mutation === 'appointment') createDemoAppointment();
    else if (mutation === 'client') createDemoClient();
    else if (mutation === 'invoice') createDemoInvoice();
    // Steps whose mutation changes no demo data must not refetch every query:
    // the resulting loading states blank out the page the tour is entering next.
    else return;
    queryClient.invalidateQueries();
  }, []);

  const advanceStep = useCallback(() => {
    if (lastAdvancedIndexRef.current === activeIndex) return;
    lastAdvancedIndexRef.current = activeIndex;
    runMutation(step?.mutation, step?.id ?? '');
    if (activeIndex < steps.length - 1) {
      goToStep(activeIndex + 1);
    } else {
      setPhase('done');
      setBox(null);
    }
  }, [activeIndex, goToStep, runMutation, step]);

  const goNext = useCallback(() => {
    if (targets.length === 0) {
      // The step just changed and its page is still rendering. A next-click here
      // used to skip the entire step; only honour it as a deliberate skip once
      // the step has had a fair chance to produce its highlights.
      if (Date.now() - stepEnteredAtRef.current >= EMPTY_STEP_MANUAL_SKIP_MS) advanceStep();
      return;
    }
    if (targetIndex < targets.length - 1) {
      setTargetIndex((current) => current + 1);
    } else {
      advanceStep();
    }
  }, [advanceStep, targetIndex, targets.length]);

  const goBack = useCallback(() => {
    lastAdvancedIndexRef.current = -1;
    if (targetIndex > 0) {
      setTargetIndex((current) => current - 1);
    } else if (activeIndex > 0) {
      goToStep(activeIndex - 1);
    }
  }, [activeIndex, goToStep, targetIndex]);

  const restartDemo = useCallback(() => {
    resetDemoState();
    queryClient.clear();
    lastAdvancedIndexRef.current = -1;
    mutationRunRef.current.clear();
    openRunRef.current.clear();
    interactionRunRef.current.clear();
    setPaused(false);
    setPhase('touring');
    goToStep(0);
  }, [goToStep]);

  const startTour = useCallback(() => {
    markIntroSeen();
    setPhase('touring');
    setPaused(false);
    goToStep(0);
  }, [goToStep]);

  const exploreFreely = useCallback(() => {
    disableDemoMode('/signup');
  }, []);

  // Resuming from pause gives the current step a fresh grace period before
  // next-clicks on a still-empty step are treated as a deliberate skip.
  useEffect(() => {
    if (!paused) stepEnteredAtRef.current = Date.now();
  }, [paused]);

  // Keep the app on the current step's route while the tour is actively running.
  useEffect(() => {
    if (!touring || paused || !step) return;
    if (`${location.pathname}${location.search}` !== step.route) {
      navigate(step.route, { replace: location.pathname === '/' || location.pathname === '/login' });
    }
  }, [touring, paused, location.pathname, location.search, navigate, step]);

  // Steps that demo a dialog click their trigger once the page has rendered it.
  useEffect(() => {
    if (!touring || paused || !step?.openSelector || openRunRef.current.has(step.id)) return;
    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts += 1;
      const trigger = findTarget(step.openSelector!);
      if (!trigger) {
        if (attempts >= EMPTY_POLL_MAX_ATTEMPTS) window.clearInterval(interval);
        return;
      }
      openRunRef.current.add(step.id);
      trigger.click();
      window.clearInterval(interval);
    }, 350);
    return () => window.clearInterval(interval);
  }, [touring, paused, location.pathname, location.search, step]);

  // Collect this step's highlights, polling while the step runs so targets that
  // render late (dialog transitions, async content) still join the sequence.
  useEffect(() => {
    if (!touring || paused || !step) return;
    let emptyAttempts = 0;
    let anyFound = false;
    const poll = () => {
      const next = collectTargets(step);
      if (next.length === 0) {
        emptyAttempts += 1;
        if (!anyFound && emptyAttempts >= EMPTY_POLL_MAX_ATTEMPTS) {
          window.clearInterval(interval);
          advanceStep();
        }
        return;
      }
      anyFound = true;
      setTargets((previous) =>
        previous.length === next.length && previous.every((entry, index) => entry.id === next[index].id)
          ? previous
          : next,
      );
    };
    const interval = window.setInterval(poll, EMPTY_POLL_MS);
    poll();
    return () => window.clearInterval(interval);
  }, [touring, paused, step, advanceStep]);

  // Track the highlighted element's position through scrolling, resizes, and layout changes.
  useEffect(() => {
    if (!touring || paused) {
      setBox(null);
      return;
    }
    const updateHighlight = () => {
      const selector = activeTarget?.selector || step?.surfaceSelector;
      const element = selector ? findTarget(selector) : null;
      if (!element) {
        setBox(null);
        return;
      }
      const rect = element.getBoundingClientRect();
      setBox({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    };
    updateHighlight();
    const interval = window.setInterval(updateHighlight, 400);
    window.addEventListener('resize', updateHighlight);
    window.addEventListener('scroll', updateHighlight, true);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('resize', updateHighlight);
      window.removeEventListener('scroll', updateHighlight, true);
    };
  }, [touring, paused, activeTarget, step]);

  // Bring the active highlight into view and trigger its hover affordances.
  useEffect(() => {
    if (!touring || paused || !activeTarget) return;
    const element = findTarget(activeTarget.selector);
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
    element.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
  }, [touring, paused, activeTarget]);

  // Scripted interactions make key flows demonstrate themselves (once per run).
  useEffect(() => {
    if (!touring || paused || !activeTarget?.interaction || !step) return;
    const key = `${step.id}:${activeTarget.id}`;
    if (interactionRunRef.current.has(key)) return;
    const timeout = window.setTimeout(() => {
      interactionRunRef.current.add(key);
      runDemoInteraction(activeTarget.interaction!);
    }, INTERACTION_DELAY_MS);
    return () => window.clearTimeout(timeout);
  }, [touring, paused, activeTarget, step]);

  // Auto-advance through highlights while the tour is playing.
  useEffect(() => {
    if (!touring || paused || targets.length === 0) return;
    const timeout = window.setTimeout(goNext, TARGET_ADVANCE_MS);
    return () => window.clearTimeout(timeout);
  }, [touring, paused, targets.length, targetIndex, activeIndex, goNext]);

  // Keyboard shortcuts: arrows to move, space to pause/resume.
  useEffect(() => {
    if (!touring) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goBack();
      } else if (event.key === ' ') {
        event.preventDefault();
        setPaused((current) => !current);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [touring, goNext, goBack]);

  if (!enabled || !step) return null;

  if (phase === 'intro') {
    return (
      <DemoOverlayCard
        eyebrow="Interactive Demo"
        title="Welcome to CalorieScience"
        body="Take a short guided tour from the profile hub into the main workspaces — dashboard, appointments, client onboarding, meal planning, recipes, questionnaires, and invoicing. Everything runs on sample data, so nothing here touches a real account."
      >
        <button type="button" className={overlayPrimaryButton} onClick={startTour}>
          Start the tour
        </button>
        <button type="button" className={overlaySecondaryButton} onClick={exploreFreely}>
          Explore on my own
        </button>
      </DemoOverlayCard>
    );
  }

  if (phase === 'done') {
    return (
      <DemoOverlayCard
        eyebrow="Tour Complete"
        title="That's CalorieScience, end to end"
        body="You've seen the full client journey — from booking and onboarding to meal plans, questionnaires, and invoicing. Replay the tour or exit demo mode."
      >
        <button type="button" className={overlayPrimaryButton} onClick={restartDemo}>
          Replay the tour
        </button>
        <button type="button" className={overlaySecondaryButton} onClick={disableDemoMode}>
          Exit demo
        </button>
      </DemoOverlayCard>
    );
  }

  return (
    <>
      {!paused && targets.length === 0 && <DemoLoadingHint stepTitle={step.title} />}
      {!paused && <DemoSpotlight box={box} />}
      {!paused && (
        <DemoTooltip
          box={box}
          target={activeTarget}
          stepTitle={step.title}
          targetIndex={targetIndex}
          targetCount={targets.length}
          isLastStep={activeIndex === steps.length - 1}
          onBack={goBack}
          onNext={goNext}
        />
      )}
      <DemoTopBar
        activeIndex={activeIndex}
        targetIndex={targetIndex}
        targetCount={targets.length}
        paused={paused}
        progress={progress}
        onBack={goBack}
        onNext={goNext}
        onTogglePause={() => setPaused((current) => !current)}
        onRestart={restartDemo}
        onExit={disableDemoMode}
      />
    </>
  );
};
